import { useMemo, useState, type FormEvent } from 'react';
import {
  NOTE_TYPES,
  NOTE_TYPE_LABELS,
  noteNeedsAttention,
  studentFullName,
  studentSortName,
  type NoteType,
  type Student,
} from '@td/shared';
import {
  useAddNote,
  useDeleteNote,
  useDeleteStudent,
  useUpdateNote,
  useUpdateStudent,
} from '../hooks/useStudents';
import { useStudentFlags } from '../hooks/useStudentFlags';
import { FlagBadge } from './FlagBadge';
import { useConfirm } from './ConfirmDialog';
import { StudentAvatar } from './StudentAvatar';
import { usePrivacy } from '../privacy/PrivacyContext';
import { Modal } from './Modal';

const NOTE_TONES: Record<NoteType, string> = {
  behavior: 'bg-rose/20 text-rust',
  academic: 'bg-sage/25 text-olive',
  contact: 'bg-mauve/20 text-mauve',
  positive: 'bg-ochre/25 text-walnut',
  medical: 'bg-clay/25 text-rust',
  general: 'bg-linen text-stone',
};

function whenLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay
    ? `Today, ${time}`
    : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' })}, ${time}`;
}

/** The full profile: photo, flags, about, and the running notes log. */
export function StudentProfile({
  student,
  onEdit,
  onClose,
  readOnly = false,
}: {
  student: Student;
  onEdit: () => void;
  onClose: () => void;
  /**
   * The archive opens this same panel, and a past year is a record rather
   * than a working file. Every control that writes is hidden — one flag here
   * rather than a second, drifting copy of the profile.
   */
  readOnly?: boolean;
}) {
  const { privacy } = usePrivacy();
  const confirm = useConfirm();
  const addNote = useAddNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();
  const removeStudent = useDeleteStudent();
  const updateStudent = useUpdateStudent();
  const { data: flagDefs = [] } = useStudentFlags();

  const [body, setBody] = useState('');
  const [type, setType] = useState<NoteType>('behavior');
  const [followUp, setFollowUp] = useState(false);
  const [filter, setFilter] = useState<NoteType | 'all' | 'followup'>('all');

  const notes = useMemo(() => {
    if (filter === 'all') return student.notes;
    if (filter === 'followup') return student.notes.filter(noteNeedsAttention);
    return student.notes.filter((n) => n.type === filter);
  }, [student.notes, filter]);

  const openFollowUps = student.notes.filter(noteNeedsAttention).length;

  async function submitNote(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    await addNote.mutateAsync({ id: student.id, type, body, needsFollowUp: followUp });
    setBody('');
    setFollowUp(false);
  }

  return (
    <Modal
      onClose={onClose}
      label={`Profile for ${studentFullName(student)}`}
      align="right"
      backdrop="bg-espresso/25"
      zIndex={40}
      className="flex h-full w-full max-w-xl flex-col overflow-y-auto bg-cream shadow-lifted"
    >
      <div className="flex min-h-full flex-col">
        <header className="flex items-start gap-3 border-b border-sand bg-linen/60 p-5">
          <StudentAvatar student={student} size={72} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl">
              {privacy ? studentSortName(student).slice(0, 1) + '.' : studentFullName(student)}
            </h2>
            <p className="text-xs text-stone">
              {student.number != null ? `#${student.number}` : 'no number while archived'}
              {student.preferredName && !privacy ? ` · ${student.firstName} on paper` : ''}
              {!student.active
                ? ` · archived${student.archivedAt ? ` ${whenLabel(student.archivedAt)}` : ''}`
                : ''}
            </p>
            {student.flags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {student.flags.map((f) => (
                  <FlagBadge key={f} flagKey={f} defs={flagDefs} full />
                ))}
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <button onClick={onClose} aria-label="Close" className="text-stone hover:text-espresso">
              ✕
            </button>
            {!readOnly && (
              <button onClick={onEdit} className="btn-soft-accent">
                Edit
              </button>
            )}
          </div>
        </header>

        <div className="space-y-5 p-5">
          {student.about.trim() && (
            <section>
              <h3 className="label">About</h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-walnut">
                {student.about}
              </p>
            </section>
          )}

          {!readOnly && (
            <section>
              <h3 className="label">Add a note</h3>
              <form onSubmit={submitNote} className="rounded-card border border-sand bg-white p-3">
                <textarea
                  className="field min-h-[64px]"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={4000}
                  placeholder="What happened, in your own words."
                />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {NOTE_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      aria-pressed={type === t}
                      className={[
                        'rounded-lg border px-2 py-1 text-[11px] transition-colors',
                        type === t
                          ? 'border-terracotta bg-terracotta text-cream'
                          : 'border-sand text-walnut hover:bg-linen',
                      ].join(' ')}
                    >
                      {NOTE_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-walnut">
                    <input
                      type="checkbox"
                      checked={followUp}
                      onChange={(e) => setFollowUp(e.target.checked)}
                      className="h-4 w-4 accent-terracotta"
                    />
                    Needs follow-up
                  </label>
                  <button
                    type="submit"
                    className="btn-primary px-3 py-1.5 text-xs"
                    disabled={!body.trim() || addNote.isPending}
                  >
                    {addNote.isPending ? 'Saving…' : 'Add note'}
                  </button>
                </div>
              </form>
            </section>
          )}

          <section>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <h3 className="label mb-0 mr-1">Log</h3>
              {(['all', 'followup', ...NOTE_TYPES] as const).map((f) => {
                const label =
                  f === 'all'
                    ? 'All'
                    : f === 'followup'
                      ? `Follow-up${openFollowUps ? ` (${openFollowUps})` : ''}`
                      : NOTE_TYPE_LABELS[f];
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    aria-pressed={filter === f}
                    className={[
                      'rounded-full px-2 py-0.5 text-[11px] transition-colors',
                      filter === f
                        ? 'bg-espresso text-cream'
                        : 'bg-linen text-walnut hover:bg-sand/60',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {notes.length === 0 ? (
              <p className="text-sm text-stone">
                {student.notes.length === 0
                  ? 'Nothing logged yet.'
                  : 'Nothing in the log matches that filter.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li
                    key={n.id}
                    className={[
                      'group rounded-card border bg-white p-3',
                      noteNeedsAttention(n) ? 'border-ochre' : 'border-sand',
                    ].join(' ')}
                  >
                    <div className="mb-1 flex items-center gap-2 text-[11px]">
                      <span className={['rounded px-1.5 py-0.5', NOTE_TONES[n.type]].join(' ')}>
                        {NOTE_TYPE_LABELS[n.type]}
                      </span>
                      <span className="text-stone">{whenLabel(n.createdAt)}</span>
                      {n.editedAt && <span className="text-stone/70">· edited</span>}
                      {!readOnly && (
                        <span className="ml-auto flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          {n.needsFollowUp && (
                            <button
                              onClick={() =>
                                updateNote.mutate({
                                  id: student.id,
                                  noteId: n.id,
                                  followedUp: !n.followedUpAt,
                                })
                              }
                              className="text-stone hover:text-olive"
                            >
                              {n.followedUpAt ? 'Reopen' : 'Mark done'}
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Delete this note?',
                                body: 'It cannot be recovered.',
                                confirmLabel: 'Delete',
                                danger: true,
                              });
                              if (ok) deleteNote.mutate({ id: student.id, noteId: n.id });
                            }}
                            className="btn-soft-danger"
                          >
                            Delete
                          </button>
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-espresso">
                      {n.body}
                    </p>
                    {n.needsFollowUp && (
                      <p className="mt-1 text-[11px] text-ochre">
                        {n.followedUpAt
                          ? `Followed up ${whenLabel(n.followedUpAt)}`
                          : 'Waiting on follow-up'}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {!readOnly && (
            <section className="flex flex-wrap items-center gap-2 border-t border-sand pt-4">
              <button
                onClick={async () => {
                  if (student.active) {
                    // Spelled out because the number is not handed back on
                    // return — by then it may well belong to someone else.
                    const ok = await confirm({
                      title: `Archive ${studentFullName(student)}?`,
                      body:
                        `Their notes and photo are kept, and you can bring them back at any time. ` +
                        `Number ${student.number} goes back to the class.`,
                      confirmLabel: 'Archive',
                    });
                    if (!ok) return;
                  }
                  updateStudent.mutate({ id: student.id, active: !student.active });
                }}
                className="btn-secondary"
              >
                {student.active ? 'Archive' : 'Bring back into class'}
              </button>

              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: `Remove ${studentFullName(student)}?`,
                    body: `Every note about them goes too, and none of it can be recovered. If they might return, archive them instead.`,
                    confirmLabel: 'Remove permanently',
                    danger: true,
                  });
                  if (ok) {
                    removeStudent.mutate(student.id);
                    onClose();
                  }
                }}
                className="btn-danger ml-auto"
              >
                Remove
              </button>
            </section>
          )}
        </div>
      </div>
    </Modal>
  );
}
